import React, { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { SVGIcon, SVGIcons } from '@/components/atoms/Icon/SVGIcon';
import { SvyButton } from '@/components/savory/SvyButton';
import Modal from '@/components/atoms/Modal';
import { computeText } from '@/locales/utils';
import { theme } from '@/core/theme';

import {
    ModalContent,
    FieldLabel,
    RequiredMark,
    FieldGroup,
    InputRow,
    DelayInput,
    UnitLabel,
    HelperText,
    ButtonContent,
} from './CarouselDelayCard.style';

type Props = {
    initialValue: number; // ms from the API
    onSave: (ms: number) => Promise<void>;
    isSaving: boolean;
};

export const CarouselDelayCard: React.FC<Props> = ({ initialValue, onSave, isSaving }) => {
    const intl = useIntl();
    const displaySeconds = Math.round(initialValue / 1000);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [seconds, setSeconds] = useState(displaySeconds);

    const openModal = useCallback(() => {
        setSeconds(displaySeconds);
        setIsModalOpen(true);
    }, [displaySeconds]);

    const closeModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const val = parseInt(e.target.value, 10);
        if (!Number.isNaN(val) && val >= 1) {
            setSeconds(val);
        } else if (e.target.value === '') {
            setSeconds(1);
        }
    }, []);

    const handleSave = useCallback(async () => {
        await onSave(seconds * 1000);
        setIsModalOpen(false);
    }, [onSave, seconds]);

    const isUnchanged = seconds === displaySeconds;

    return (
        <>
            <SvyButton
                onClick={openModal}
                size="s"
                variant="outline"
                data-testid="carousel-delay-edit"
                style={{
                    marginRight: 12,
                    borderColor: theme.savory.colors.secondary[500],
                    color: theme.savory.colors.secondary[500],
                }}
            >
                <ButtonContent>
                    <SVGIcon icon={SVGIcons.CRONOMETER} size={16} color={theme.savory.colors.secondary[500]} />
                    {displaySeconds}s
                </ButtonContent>
            </SvyButton>

            <Modal
                isOpen={isModalOpen}
                onHide={closeModal}
                title={computeText(intl, 'brandPromotionalBanner.carousel.title')}
                buttons={
                    <SvyButton
                        onClick={handleSave}
                        disabled={isUnchanged || isSaving}
                        data-testid="carousel-delay-save"
                    >
                        <FormattedMessage id="brandPromotionalBanner.carousel.save" />
                    </SvyButton>
                }
            >
                <ModalContent>
                    <FieldGroup>
                        <FieldLabel>
                            <FormattedMessage id="brandPromotionalBanner.carousel.description" />
                            <RequiredMark>*</RequiredMark>
                        </FieldLabel>
                        <InputRow>
                            <DelayInput
                                type="number"
                                min={1}
                                value={seconds}
                                onChange={handleChange}
                                data-testid="carousel-delay-input"
                            />
                            <UnitLabel>
                                <FormattedMessage id="brandPromotionalBanner.carousel.seconds" />
                            </UnitLabel>
                        </InputRow>
                        <HelperText>
                            <FormattedMessage id="brandPromotionalBanner.carousel.helper" />
                        </HelperText>
                    </FieldGroup>
                </ModalContent>
            </Modal>
        </>
    );
};
